// Walks a parsed formula AST against a single data row.
// Missing or non-numeric column values count as 0 (Excel-like).
// Division by zero, or any non-finite intermediate result, makes the whole result null.

class NullResult extends Error {}

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const truthy = (value) => value !== 0;

const round = (value, digits = 0) => {
  const factor = 10 ** Math.trunc(digits);
  return Math.round(value * factor) / factor;
};

const BINARY_OPS = {
  '+': (a, b) => a + b,
  '-': (a, b) => a - b,
  '*': (a, b) => a * b,
  '/': (a, b) => {
    if (b === 0) throw new NullResult();
    return a / b;
  },
  '<': (a, b) => Number(a < b),
  '<=': (a, b) => Number(a <= b),
  '>': (a, b) => Number(a > b),
  '>=': (a, b) => Number(a >= b),
  '=': (a, b) => Number(a === b),
  '<>': (a, b) => Number(a !== b),
};

const evaluateNode = (node, row) => {
  switch (node.type) {
    case 'number':
      return node.value;
    case 'ref':
      return toNumber(row?.[node.accessor]);
    case 'unary':
      return -evaluateNode(node.arg, row);
    case 'binary':
      return BINARY_OPS[node.op](evaluateNode(node.left, row), evaluateNode(node.right, row));
    case 'call':
      return evaluateCall(node, row);
    default:
      throw new NullResult();
  }
};

const evaluateCall = ({ name, args }, row) => {
  // IF, AND and OR short-circuit, so only the needed branches are evaluated
  switch (name) {
    case 'IF':
      return truthy(evaluateNode(args[0], row)) ? evaluateNode(args[1], row) : evaluateNode(args[2], row);
    case 'AND':
      return Number(args.every(arg => truthy(evaluateNode(arg, row))));
    case 'OR':
      return Number(args.some(arg => truthy(evaluateNode(arg, row))));
  }

  const values = args.map(arg => evaluateNode(arg, row));
  switch (name) {
    case 'NOT':
      return Number(!truthy(values[0]));
    case 'ABS':
      return Math.abs(values[0]);
    case 'MIN':
      return Math.min(...values);
    case 'MAX':
      return Math.max(...values);
    case 'ROUND':
      return round(values[0], values[1]);
    default:
      throw new NullResult();
  }
};

const evaluate = (ast, row) => {
  try {
    const result = evaluateNode(ast, row);
    return Number.isFinite(result) ? result : null;
  } catch (e) {
    if (e instanceof NullResult) return null;
    throw e;
  }
};

export default evaluate;
